import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { 
  Clock, 
  Calendar, 
  Star, 
  Zap, 
  Coffee, 
  Wifi, 
  Monitor,
  Users,
  Package as PackageIcon,
  CheckCircle
} from 'lucide-react';

interface Package {
  id: number;
  name: string;
  type: string;
  price: number;
  originalPrice?: number;
  hours: number | 'unlimited';
  duration: string;
  description: string;
  features: string[];
  popular: boolean;
  color: string;
}

interface MemberDashboardProps {
  currentUser: {
    username: string;
    role: string;
    fullName: string;
    avatar: string;
  };
}

export function MemberDashboard({ currentUser }: MemberDashboardProps) {
  const [packages] = useState<Package[]>([
    {
      id: 1,
      name: 'Hourly Pass',
      type: 'hourly',
      price: 825,
      hours: 1,
      duration: 'Per Hour',
      description: 'Perfect for quick work sessions or meetings',
      features: ['High-speed WiFi', 'Power outlets', 'Coffee/Tea', 'Printing access'],
      popular: false,
      color: 'bg-green-100 text-green-800'
    },
    {
      id: 2,
      name: 'Daily Pass',
      type: 'daily',
      price: 1925,
      originalPrice: 2200,
      hours: 'unlimited',
      duration: 'Full Day',
      description: 'Unlimited access for the entire day',
      features: ['High-speed WiFi', 'Power outlets', 'Coffee/Tea', 'Printing access', 'Meeting room access', 'Locker storage'],
      popular: true,
      color: 'bg-orange-100 text-orange-800'
    },
    {
      id: 3,
      name: '10-Hour Package',
      type: 'package',
      price: 7500,
      originalPrice: 8250,
      hours: 10,
      duration: '30 Days Validity',
      description: 'Flexible hours for occasional users',
      features: ['High-speed WiFi', 'Power outlets', 'Coffee/Tea', 'Printing access', 'Meeting room access'],
      popular: false,
      color: 'bg-blue-100 text-blue-800'
    },
    {
      id: 4,
      name: '30-Hour Package',
      type: 'package',
      price: 19500,
      originalPrice: 24750,
      hours: 30,
      duration: '60 Days Validity',
      description: 'Great value for regular users',
      features: ['High-speed WiFi', 'Power outlets', 'Coffee/Tea', 'Printing access', 'Meeting room access', 'Priority booking'],
      popular: true,
      color: 'bg-purple-100 text-purple-800'
    },
    {
      id: 5,
      name: 'Monthly Regular',
      type: 'monthly',
      price: 8500,
      originalPrice: 12000,
      hours: 'unlimited',
      duration: 'Monthly',
      description: 'Unlimited access with great savings',
      features: ['High-speed WiFi', 'Power outlets', 'Coffee/Tea', 'Printing access', 'Meeting room access', 'Locker storage', '24/7 access'],
      popular: false,
      color: 'bg-blue-100 text-blue-800'
    },
    {
      id: 6,
      name: 'Monthly Premium',
      type: 'monthly',
      price: 12500,
      originalPrice: 18000,
      hours: 'unlimited',
      duration: 'Monthly',
      description: 'Premium experience with all amenities',
      features: ['High-speed WiFi', 'Power outlets', 'Premium coffee/Tea', 'Unlimited printing', 'Private meeting rooms', 'Dedicated locker', '24/7 access', 'Guest passes'],
      popular: true,
      color: 'bg-purple-100 text-purple-800'
    }
  ]);

  const getFeatureIcon = (feature: string) => {
    if (feature.includes('WiFi')) return <Wifi className="h-4 w-4" />;
    if (feature.includes('Coffee') || feature.includes('Tea')) return <Coffee className="h-4 w-4" />;
    if (feature.includes('meeting') || feature.includes('Meeting')) return <Users className="h-4 w-4" />;
    if (feature.includes('access')) return <Clock className="h-4 w-4" />;
    if (feature.includes('printing') || feature.includes('Printing')) return <Monitor className="h-4 w-4" />;
    return <CheckCircle className="h-4 w-4" />;
  };

  const formatPrice = (price: number) => {
    return `₱${price.toLocaleString()}`;
  };

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="space-y-4">
        <div className="flex items-center space-x-4 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border">
          <Avatar className="h-16 w-16">
            <AvatarImage src={currentUser.avatar} alt={currentUser.fullName} />
            <AvatarFallback>
              {currentUser.fullName.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h1 className="text-2xl">Welcome back, {currentUser.fullName}!</h1>
            <p className="text-muted-foreground">
              Explore our flexible workspace packages designed for your productivity
            </p>
          </div>
          <Badge variant="secondary" className="bg-blue-100 text-blue-800">
            {currentUser.role}
          </Badge>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Available Packages</CardTitle>
            <PackageIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">{packages.length}</div>
            <p className="text-xs text-muted-foreground">
              Flexible options available
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Starting From</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">₱825</div>
            <p className="text-xs text-muted-foreground">
              Per hour
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm">Best Value</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl">Monthly</div>
            <p className="text-xs text-muted-foreground">
              Premium packages
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Packages Section */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2>Workspace Packages</h2>
            <p className="text-muted-foreground">Choose the perfect plan for your needs</p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {packages.map((pkg) => (
            <Card key={pkg.id} className={`relative ${pkg.popular ? 'ring-2 ring-blue-500 shadow-lg' : ''}`}>
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-blue-600 text-white">
                    <Star className="mr-1 h-3 w-3" />
                    Popular
                  </Badge>
                </div>
              )}
              
              <CardHeader className="text-center pb-4">
                <div className="flex justify-center mb-2">
                  <Badge className={pkg.color} variant="secondary">
                    {pkg.type.charAt(0).toUpperCase() + pkg.type.slice(1)}
                  </Badge>
                </div>
                <CardTitle className="text-xl">{pkg.name}</CardTitle>
                <CardDescription>{pkg.description}</CardDescription>
                
                <div className="pt-4">
                  <div className="flex items-center justify-center space-x-2">
                    <span className="text-3xl font-bold">{formatPrice(pkg.price)}</span>
                    {pkg.originalPrice && (
                      <span className="text-lg text-muted-foreground line-through">
                        {formatPrice(pkg.originalPrice)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-center space-x-2 mt-1">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      {pkg.hours === 'unlimited' ? 'Unlimited hours' : `${pkg.hours} hours`} • {pkg.duration}
                    </span>
                  </div>
                  {pkg.originalPrice && (
                    <div className="mt-2">
                      <Badge variant="outline" className="text-green-700 border-green-200 bg-green-50">
                        Save {formatPrice(pkg.originalPrice - pkg.price)}
                      </Badge>
                    </div>
                  )}
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <h4 className="font-medium">What's included:</h4>
                  <ul className="space-y-2">
                    {pkg.features.map((feature, index) => (
                      <li key={index} className="flex items-center space-x-2 text-sm">
                        {getFeatureIcon(feature)}
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <Button 
                  className={`w-full ${pkg.popular ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                >
                  {pkg.type === 'monthly' ? 'Subscribe Now' : 
                   pkg.type === 'package' ? 'Purchase Package' : 
                   'Book Now'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Additional Information */}
      <Card>
        <CardHeader>
          <CardTitle>Need Help Choosing?</CardTitle>
          <CardDescription>Contact our team for personalized recommendations</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <h4 className="font-medium">For Occasional Use</h4>
              <p className="text-sm text-muted-foreground">
                Hourly or Daily passes are perfect for meetings, interviews, or short work sessions.
              </p>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium">For Regular Users</h4>
              <p className="text-sm text-muted-foreground">
                Hour packages and monthly plans offer better value with additional perks and flexibility.
              </p>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 pt-4">
            <Button variant="outline" size="sm">
              <Coffee className="mr-2 h-4 w-4" />
              Schedule a Tour
            </Button>
            <Button variant="outline" size="sm">
              <Users className="mr-2 h-4 w-4" />
              Contact Support
            </Button>
            <Button variant="outline" size="sm">
              <Calendar className="mr-2 h-4 w-4" />
              Book a Day Pass
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}